import { IRunsStat } from '@/api/resort/types';

// The API distinguishes "nothing is open" from "the resort didn't report
// a count": `{ open: 0 }` vs `{ open: null }`. Both used to be collapsed
// with `?? 0`, which told users a fully-open resort had zero runs open
// (Ohau/777 and Cardrona report `runs.open: null` mid-season, the latter
// alongside `openPercent: 100`).
//
// These helpers keep the two apart everywhere an open count is rendered
// or sorted. They mirror util/baseDepthRange: render a dash when there's
// nothing to show, and sort unknowns to the bottom in either direction.

type OpenStat = { open?: number | null; total?: number };

/**
 * Whether the resort actually reported an open count. A reported `0` is
 * data and passes; `null`/`undefined` is an absence and does not.
 */
export const hasOpenCount = (stat?: OpenStat | null): boolean => typeof stat?.open === 'number';

/**
 * The reported open count, or `undefined` when unreported. Callers render
 * a dash for `undefined` rather than substituting a zero.
 */
export const openCount = (stat?: OpenStat | null): number | undefined => (
  typeof stat?.open === 'number' ? stat.open : undefined
);

/**
 * The "N / total" reading as a plain string, for surfaces that can't take
 * the `OpenCount` component (stat blocks, mobile card values). Renders
 * `dash` for the unreported half, and for the whole reading when there is
 * no total to compare against.
 */
export const openCountText = (stat: OpenStat | undefined, dash: string): string => (
  typeof stat?.total !== 'number' ? dash : `${openCount(stat) ?? dash} / ${stat.total}`
);

/**
 * The number an "open" column sorts on: the reported count, or
 * `undefined` for rows that render a dash so they group at the bottom
 * whichever way the column is sorted — the same contract the base-depth
 * column uses (see util/baseDepthRange.baseDepthSortValue).
 *
 * A row with no `total` renders a dash for the whole cell, so it sorts as
 * missing even when a count is present, keeping the order honest to what
 * is on screen.
 */
export const openCountSortValue = (stat?: OpenStat | null): number | undefined => (
  typeof stat?.total === 'number' ? openCount(stat) : undefined
);

/**
 * Percentage of runs open, preferring the figure the API reports over one
 * derived from the open count. Returns `undefined` when the resort has
 * reported neither, so the stat can be hidden instead of showing a
 * misleading 0%.
 */
export const runsOpenPercent = (runs?: IRunsStat | null): number | undefined => {
  if (typeof runs?.openPercent === 'number') return Math.round(runs.openPercent);
  const open = openCount(runs);
  if (open === undefined || !runs?.total) return undefined;
  return Math.round((open / runs.total) * 100);
};
